import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';

export interface CrewAppointment {
  id: string;
  startAt: string | null;
  endAt: string | null;
  status: string;
  customerName: string;
  title: string;
  jobId: string | null;
  jobName: string | null;
  /** Service-address zone; display metadata, null on ungeocoded rows. */
  siteTimezone?: string | null;
}

export interface CrewConflict {
  technicianId: string;
  technicianName: string;
  appointments: {
    id: string;
    startAt: string | null;
    endAt: string | null;
    customerName: string;
  }[];
  blocked: {
    id: string;
    startAt: string;
    endAt: string;
    reason: string | null;
    source: string;
  }[];
}

export interface CrewConflictResult {
  hasConflicts: boolean;
  conflicts: CrewConflict[];
}

export function useCrewAppointments(crewId: string | undefined) {
  return useApiQuery<CrewAppointment[]>({
    queryKey: [...schedulerKeys.crews(), crewId ?? '', 'appointments'],
    queryFn: () =>
      apiGet<CrewAppointment[]>(
        operationsClient,
        schedulerEndpoints.crewAppointments(crewId ?? ''),
      ),
    enabled: Boolean(crewId),
    showErrorToast: false,
  });
}

/** Per-member scheduling conflicts for a candidate window (enabled when set). */
export function useCrewConflicts(
  crewId: string | undefined,
  window: { start?: string; end?: string; excludeAppointmentId?: string },
) {
  return useApiQuery<CrewConflictResult>({
    queryKey: [
      ...schedulerKeys.crews(),
      crewId ?? '',
      'conflicts',
      window.start ?? '',
      window.end ?? '',
      window.excludeAppointmentId ?? '',
    ],
    queryFn: () =>
      apiGet<CrewConflictResult>(operationsClient, schedulerEndpoints.crewConflicts(crewId ?? ''), {
        params: {
          start: window.start,
          end: window.end,
          ...(window.excludeAppointmentId && {
            excludeAppointmentId: window.excludeAppointmentId,
          }),
        },
      }),
    enabled: Boolean(crewId && window.start && window.end),
    showErrorToast: false,
  });
}
